import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import HiredLogo from "@/components/brand/HiredLogo";
import { ChevronDown } from "lucide-react";

const EMPLOYEES = [
  { to: "/hired/hoa-steward",      name: "HOA Steward",      industry: "Community Management",         desc: "Runs the HOA back office — inbox, violations, ARC, board summaries.", testid: "dd-item-steward" },
  { to: "/hired/homihq",           name: "HomiHQ",           industry: "Real Estate",                   desc: "Leads, listings, transactions and showings kept moving.", testid: "dd-item-homihq" },
  { to: "/hired/foundation-first", name: "Foundation First", industry: "Construction & Home Services",  desc: "Job-site paperwork, permits, subs, and 811 locate assistance.", testid: "dd-item-foundation" },
];

export default function PublicNav() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [openMenu, setOpenMenu] = useState(null); // 'employees' | null
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenMenu(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => { setOpenMenu(null); }, [loc.pathname]);

  const linkCls = (active) => `text-sm tracking-wide transition-colors inline-flex items-center gap-1 ${active ? 'text-ivory' : 'text-steel hover:text-ivory'}`;

  const goHowItWorks = (e) => {
    e.preventDefault();
    if (loc.pathname === "/") {
      const el = document.getElementById("how");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      nav("/#how");
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-obsidian/80 backdrop-blur-xl border-b border-line">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center">
          <HiredLogo size={22} />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {/* Employees dropdown */}
          <div className="relative">
            <button
              data-testid="nav-employees"
              onClick={() => setOpenMenu(openMenu === "employees" ? null : "employees")}
              className={linkCls(openMenu === "employees")}
              aria-expanded={openMenu === "employees"}
              aria-haspopup="menu"
            >
              Employees <ChevronDown size={14} className={`transition-transform ${openMenu === "employees" ? "rotate-180" : ""}`} />
            </button>
            {openMenu === "employees" && (
              <div data-testid="dd-panel-employees" className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[520px] bg-graphite border border-line shadow-2xl">
                <div className="p-4 border-b border-line brand-eyebrow">MEET THE EMPLOYEES</div>
                <div className="divide-y divide-line">
                  {EMPLOYEES.map(p => (
                    <Link
                      key={p.to}
                      to={p.to}
                      data-testid={p.testid}
                      onClick={() => setOpenMenu(null)}
                      className="flex items-start gap-4 p-4 hover:bg-obsidian transition-colors"
                    >
                      <div className="w-1 h-12 bg-gold shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-ivory font-semibold">{p.name}</div>
                        <div className="text-[10px] tracking-widest text-gold mt-0.5">{p.industry.toUpperCase()}</div>
                        <div className="text-steel text-sm mt-1">{p.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a href="/#how" data-testid="nav-how-it-works" onClick={goHowItWorks} className={linkCls(false)}>How It Works</a>
          <Link to="/pricing" data-testid="nav-pricing" className={linkCls(loc.pathname === "/pricing")}>Pricing</Link>
          <Link to="/about"   data-testid="nav-about"   className={linkCls(loc.pathname === "/about")}>About</Link>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard" className="btn-purple text-sm px-5 py-3">Open Dashboard</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline text-sm text-steel hover:text-ivory">Log in</Link>
              <Link to="/signup" data-testid="nav-signup" className="btn-purple text-sm px-5 py-3">Get Started</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
